import { useState, useMemo, useEffect } from 'react';
import { getTokenLogoUrl, getTokenColor } from '../lib/tokenLogos';
import { getNetworkLogoUrl, getNetworkColor } from '../lib/networkLogos';
import { TokenIcon } from './ui/TokenIcon';
import { NetworkIcon } from './ui/NetworkIcon';

interface Chain {
  code: string;
  name: string;
  validated: boolean;
  enabled: boolean;
}

interface TokenWithChains {
  symbol: string;
  name: string;
  id?: string; // CoinGecko ID from API
  defaultNetwork: string;
  chains: Chain[];
}

interface TokenChainSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  cryptoChannels: any;
  selectedToken: string;
  selectedChain: string;
  onSelect: (token: string, chain: string) => void;
  mode: 'ONRAMP' | 'OFFRAMP';
}

export function TokenChainSelectionModal({
  isOpen,
  onClose,
  cryptoChannels,
  selectedToken,
  selectedChain,
  onSelect,
  mode,
}: TokenChainSelectionModalProps) {
  const [tokenSearch, setTokenSearch] = useState('');
  const [chainSearch, setChainSearch] = useState('');
  // Default to 'all' networks
  const [selectedChainFilter, setSelectedChainFilter] = useState<string>('all');
  // Mobile chain modal state
  const [isMobileChainModalOpen, setIsMobileChainModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Transform channels data into token-chain matrix
  const { tokens, chains } = useMemo(() => {
    if (!cryptoChannels?.channels) {
      return { tokens: [], chains: [] };
    }

    const tokensWithChains: TokenWithChains[] = cryptoChannels.channels
      .filter((channel: any) =>
        channel.enabled && (!channel.validationStatus || channel.validationStatus === 'validated')
      )
      .map((channel: any) => ({
        symbol: channel.code,
        name: channel.name || channel.code,
        defaultNetwork: channel.defaultNetwork,
        chains: Object.values(channel.networks || {})
          .filter((net: any) => {
            if ('validated' in net) {
              return net.validated === true && net.enabled !== false;
            }
            return true;
          })
          .map((net: any) => ({
            code: net.network,
            name: net.name || net.network,
            validated: net.validated !== false,
            enabled: net.enabled !== false,
          })),
      }))
      .filter((token: TokenWithChains) => token.chains.length > 0);

    // Get unique chains
    const chainSet = new Map<string, string>();
    tokensWithChains.forEach((token) => {
      token.chains.forEach((chain) => {
        if (!chainSet.has(chain.code)) {
          chainSet.set(chain.code, chain.name);
        }
      });
    });

    const uniqueChains = Array.from(chainSet.entries()).map(([code, name]) => ({
      code,
      name,
    }));

    // Sort chains with predefined order
    const chainPriority: Record<string, number> = {
      // Tron
      'TRC20': 1,
      'TRON': 1,
      'TRX': 1,
      // Solana
      'SOL': 2,
      'SOLANA': 2,
      // Ethereum
      'ERC20': 3,
      'ETH': 3,
      'ETHEREUM': 3,
      // Base
      'BASE': 4,
      // Bitcoin
      'BTC': 5,
      'BITCOIN': 5,
    };

    const sortedChains = uniqueChains.sort((a, b) => {
      const priorityA = chainPriority[a.code.toUpperCase()] || 999;
      const priorityB = chainPriority[b.code.toUpperCase()] || 999;

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // If same priority (including "others"), sort alphabetically
      return a.name.localeCompare(b.name);
    });

    return { tokens: tokensWithChains, chains: sortedChains };
  }, [cryptoChannels]);

  // Filter tokens by selected chain
  const filteredTokens = useMemo(() => {
    let filtered = tokens;

    // Filter by chain
    if (selectedChainFilter !== 'all') {
      filtered = filtered.filter((token) =>
        token.chains.some((chain) => chain.code === selectedChainFilter)
      );
    }

    // Filter by search
    if (tokenSearch) {
      const search = tokenSearch.toLowerCase();
      filtered = filtered.filter(
        (token) =>
          token.symbol.toLowerCase().includes(search) ||
          token.name.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [tokens, selectedChainFilter, tokenSearch]);

  // Filter chains by search
  const filteredChains = useMemo(() => {
    if (!chainSearch) return chains;
    const search = chainSearch.toLowerCase();
    return chains.filter(
      (chain) =>
        chain.code.toLowerCase().includes(search) ||
        chain.name.toLowerCase().includes(search)
    );
  }, [chains, chainSearch]);

  const handleTokenSelect = (token: TokenWithChains) => {
    // Preserve chain selection intelligently:
    // 1. If a specific chain filter is active, use it
    // 2. If user is reselecting the same token with 'all' filter, preserve their current chain
    // 3. Otherwise, use the token's default network or first available chain
    let chain: string;

    if (selectedChainFilter !== 'all') {
      // Chain filter is active - use it
      chain = selectedChainFilter;
    } else if (token.symbol === selectedToken && selectedChain) {
      // Same token, no filter - preserve current chain if it's valid for this token
      const isChainValid = token.chains.some(c => c.code === selectedChain);
      chain = isChainValid ? selectedChain : (token.defaultNetwork || token.chains[0]?.code);
    } else {
      // Different token or no current selection - use default
      chain = token.defaultNetwork || token.chains[0]?.code;
    }

    onSelect(token.symbol, chain);
    onClose();
  };

  const handleMobileChainSelect = (chainCode: string) => {
    setSelectedChainFilter(chainCode);
    setIsMobileChainModalOpen(false);
  };

  const getTokenColor = (symbol: string): string => {
    // Token color mapping
    const colors: Record<string, string> = {
      USDT: '#26A17B',
      USDC: '#2775CA',
      BTC: '#F7931A',
      ETH: '#627EEA',
      SOL: '#14F195',
      MATIC: '#8247E5',
      BNB: '#F3BA2F',
      AVAX: '#E84142',
      DAI: '#F5AC37',
      BUSD: '#F0B90B',
    };
    return colors[symbol] || '#64748B';
  };

  if (!isOpen) return null;

  return (
    <div
      className="token-selection-modal"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left Panel - Token Selection */}
        <div className="modal-left-panel">
          {/* Header */}
          <div className="modal-header">
            <button onClick={onClose} className="modal-back-button" aria-label="Go back">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h2 id="modal-title" className="modal-title">
              {mode === 'ONRAMP' ? 'Exchange from' : 'Exchange from'}
            </h2>
          </div>

          {/* Token Search */}
          <div className="modal-search">
            <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search by token or address"
              value={tokenSearch}
              onChange={(e) => setTokenSearch(e.target.value)}
              className="modal-search-input"
            />
          </div>

          {/* Token List */}
          <div className="token-list">
            {filteredTokens.length === 0 ? (
              <div className="text-center py-8 text-[var(--fg-3)]">
                No tokens found
              </div>
            ) : (
              filteredTokens.map((token) => {
                const availableChains = token.chains.filter(
                  (chain) =>
                    selectedChainFilter === 'all' || chain.code === selectedChainFilter
                );

                return (
                  <div
                    key={token.symbol}
                    role="button"
                    tabIndex={0}
                    className="token-item"
                    onClick={() => handleTokenSelect(token)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleTokenSelect(token);
                      }
                    }}
                    aria-label={`Select ${token.name} (${token.symbol}) with ${availableChains.length} available network${availableChains.length !== 1 ? 's' : ''}`}
                  >
                    <div className="token-icon-wrapper">
                      <TokenIcon symbol={token.symbol} className="token-icon" />
                    </div>
                    <div className="token-info">
                      <div className="token-symbol">{token.symbol}</div>
                      <div className="token-name">{token.name}</div>
                    </div>
                    <div className="token-balance">
                      <div className="token-balance-amount">0.00</div>
                      <div className="token-balance-usd">$0.00</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Panel - Chain Selection */}
        <div className="modal-right-panel">
          {/* Chain Search */}
          <div className="chain-search">
            <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search network"
              value={chainSearch}
              onChange={(e) => setChainSearch(e.target.value)}
              className="chain-search-input"
            />
          </div>

          {/* Chain List */}
          <div className="chain-list">
            {/* All Networks Option */}
            <button
              className={`chain-item ${selectedChainFilter === 'all' ? 'chain-item-active' : ''}`}
              onClick={() => setSelectedChainFilter('all')}
            >
              <div className="chain-icon-multi">
                <span className="chain-multi-1"></span>
                <span className="chain-multi-2"></span>
                <span className="chain-multi-3"></span>
              </div>
              <span className="chain-name">All networks</span>
            </button>

            {/* Individual Chains */}
            {filteredChains.map((chain) => {
              return (
                <button
                  key={chain.code}
                  className={`chain-item ${selectedChainFilter === chain.code ? 'chain-item-active' : ''}`}
                  onClick={() => setSelectedChainFilter(chain.code)}
                >
                  <NetworkIcon networkCode={chain.code} className="chain-icon-single" />
                  <span className="chain-name">{chain.name}</span>
                </button>
              );
            })}

            {/* Chevron icon for mobile - opens vertical modal */}
            {isMobile && (
              <button
                className="mobile-chain-chevron"
                onClick={() => setIsMobileChainModalOpen(true)}
                aria-label="View all networks"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Chain Selection Modal */}
      {isMobile && isMobileChainModalOpen && (
        <div
          className="mobile-chain-modal-overlay"
          onClick={() => setIsMobileChainModalOpen(false)}
        >
          <div
            className="mobile-chain-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mobile-chain-modal-header">
              Select Network
            </div>
            <div className="mobile-chain-modal-list">
              {/* All Networks Option */}
              <div
                className={`mobile-chain-item ${selectedChainFilter === 'all' ? 'mobile-chain-item-active' : ''}`}
                onClick={() => handleMobileChainSelect('all')}
              >
                <div className="mobile-chain-item-icon">
                  <div className="chain-icon-multi">
                    <span className="chain-multi-1"></span>
                    <span className="chain-multi-2"></span>
                    <span className="chain-multi-3"></span>
                  </div>
                </div>
                <div className="mobile-chain-item-name">All Networks</div>
              </div>

              {/* Individual Chains */}
              {filteredChains.map((chain) => {
                return (
                  <div
                    key={chain.code}
                    className={`mobile-chain-item ${selectedChainFilter === chain.code ? 'mobile-chain-item-active' : ''}`}
                    onClick={() => handleMobileChainSelect(chain.code)}
                  >
                    <div className="mobile-chain-item-icon">
                      <NetworkIcon networkCode={chain.code} className="chain-icon-single" />
                    </div>
                    <div className="mobile-chain-item-name">{chain.name}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
